/**
 * useStripePaymentElement
 *
 * Mounts Stripe's unified **Payment Element** imperatively (no
 * `@stripe/react-stripe-js` dependency) and exposes `confirmSetup()`.
 *
 * The element renders whatever the SetupIntent enables — a card intent renders the
 * card fields; a `us_bank_account` (ACH) or card+bank intent additionally renders
 * the Financial Connections connect step AND the Nacha mandate. This is the single
 * card + bank collection path for every app (see
 * docs/refactors/payment/PAYMENT_ELEMENT_MIGRATION.md).
 *
 * Requires BOTH `publishableKey` and `clientSecret` to mount — `elements` is
 * created with the client secret, and `confirmSetup` confirms against it.
 *
 * @module hooks/payment/useStripePaymentElement
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { isStripeInfrastructureError } from '@butter/library/shared'
import { toPaymentMethodType } from '@butter/library/payment'
import type { ConfirmSetupResult, PaymentErrorCode } from './types'
import { getStripeInstance, type StripeInstance, type StripeElements } from './stripeInstance'

export interface UseStripePaymentElementOptions {
  /** Stripe publishable key — hook is a no-op (not ready) when absent. */
  publishableKey?: string
  /** SetupIntent client secret the element renders + confirms against. */
  clientSecret?: string
  /** Optional Stripe Elements `appearance` object to theme the element. */
  appearance?: object
  /**
   * Prefill values forwarded to the Payment Element. These apply ONLY to fields the
   * element actually renders — we pass no `fields` option, so a card element shows no
   * name row and `billingDetails.name` is therefore inert (the saved PaymentMethod's
   * `billing_details.name` ends up empty at Stripe). Collecting a name would require
   * opting into `fields: { billingDetails: { name: 'auto' } }`, which adds a visible
   * input on every surface.
   */
  defaultValues?: { billingDetails?: { name?: string; email?: string } }
  /**
   * `return_url` for `confirmSetup`. Required by Stripe even when
   * `redirect: 'if_required'` avoids navigation; defaults to the current URL.
   */
  returnUrl?: string
  /**
   * Called with the neutral selected method (`'card' | 'bank'`) whenever the user
   * switches rows in the Payment Element accordion. Fires once on mount with the
   * initial selection, then only when the selected type actually changes. Lets a
   * caller make a fee/total line react to the chosen method (card surcharge vs ACH).
   */
  onMethodChange?: (method: 'card' | 'bank') => void
}

export interface UseStripePaymentElementResult {
  /** Attach to the `<div>` where the Payment Element should mount. */
  paymentElementRef: React.RefObject<HTMLDivElement | null>
  /** True once the element has mounted and `confirmSetup` can run. */
  isReady: boolean
  /**
   * True once the selected method's fields are filled and valid (Stripe's `change`
   * event `complete`). **Gate your submit button on this** — confirming an
   * incomplete element just burns a round-trip on a validation failure. Resets to
   * `false` whenever the element remounts (new `clientSecret`).
   */
  isComplete: boolean
  /** True while Stripe.js / the element is loading. */
  isLoading: boolean
  /** Latest error message, or null. */
  error: string | null
  /** Machine-readable error code (SDK load/init failures). */
  errorCode: PaymentErrorCode | null
  /** Confirm the SetupIntent via the mounted element. Never throws. */
  confirmSetup: () => Promise<ConfirmSetupResult>
}

export function useStripePaymentElement({
  publishableKey,
  clientSecret,
  appearance,
  defaultValues,
  returnUrl,
  onMethodChange,
}: UseStripePaymentElementOptions = {}): UseStripePaymentElementResult {
  const isConfigured = !!publishableKey && !!clientSecret

  const paymentElementRef = useRef<HTMLDivElement>(null)
  const stripeRef = useRef<StripeInstance | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const destroyedRef = useRef(false)

  // Held in a ref so changing prefill/return-url identity doesn't remount.
  const defaultValuesRef = useRef(defaultValues)
  defaultValuesRef.current = defaultValues
  const returnUrlRef = useRef(returnUrl)
  returnUrlRef.current = returnUrl
  const appearanceRef = useRef(appearance)
  appearanceRef.current = appearance
  // onMethodChange in a ref so a new callback identity doesn't remount the element.
  const onMethodChangeRef = useRef(onMethodChange)
  onMethodChangeRef.current = onMethodChange
  // Last emitted neutral method — only notify the caller when the selection changes.
  const lastMethodRef = useRef<'card' | 'bank' | null>(null)

  const [isLoading, setIsLoading] = useState(isConfigured)
  const [isReady, setIsReady] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<PaymentErrorCode | null>(null)

  // Remount only when the account (key) or the intent (clientSecret) changes.
  useEffect(() => {
    if (!isConfigured || !publishableKey || !clientSecret) {
      // Going configured → unconfigured tears the element down (the cleanup below
      // destroys it and nulls elementsRef) but touches no React state. Without this
      // reset, isReady/isComplete stay true over a form that no longer exists — admin
      // keeps this hook mounted across the whole drawer view, so its Save button stays
      // enabled and a click confirms against a null elements ref, producing a FALSE
      // infrastructure alert. React bails out when the values already match, so this
      // costs nothing on the common first-mount path.
      setIsLoading(false)
      setIsReady(false)
      setIsComplete(false)
      setError(null)
      setErrorCode(null)
      lastMethodRef.current = null
      return
    }

    destroyedRef.current = false
    let cancelled = false
    let element: ReturnType<StripeElements['create']> | null = null

    async function init() {
      setIsLoading(true)
      setIsReady(false)
      // A fresh element starts empty. Without this reset a remount (new clientSecret —
      // e.g. admin's retryStripeSetupIntent after a failed persist) would keep the old
      // element's `true` and leave Save enabled over blank fields.
      setIsComplete(false)
      setError(null)
      setErrorCode(null)
      lastMethodRef.current = null // fresh element → re-emit its initial method

      const stripe = await getStripeInstance(publishableKey!)
      if (cancelled || destroyedRef.current) return
      if (!stripe) {
        setError('Failed to load Stripe. Please check your connection and try again.')
        setErrorCode('sdk_load_failed')
        setIsLoading(false)
        return
      }
      stripeRef.current = stripe

      const target = paymentElementRef.current
      if (!target) {
        setError('Payment container not found. Please try again.')
        setErrorCode('sdk_error')
        setIsLoading(false)
        return
      }

      try {
        const elements = stripe.elements({
          clientSecret: clientSecret!,
          ...(appearanceRef.current ? { appearance: appearanceRef.current } : {}),
        })
        elementsRef.current = elements
        const paymentElement = elements.create('payment', {
          // Link's inline signup asks for an email and a mobile number inside the
          // payment form. Our flows already collect the member's email, so the
          // second ask reads as a duplicate — product asked for it gone. It has to
          // be suppressed here rather than in the Stripe Dashboard: every club is
          // its own Stripe account (createSetupIntent resolves per-club
          // publishable/secret keys), so a Dashboard toggle would be per-club.
          wallets: { link: 'never' },
          ...(defaultValuesRef.current ? { defaultValues: defaultValuesRef.current } : {}),
        })
        paymentElement.on('ready', () => {
          if (cancelled || destroyedRef.current) return
          setIsReady(true)
          setIsLoading(false)
        })
        paymentElement.on('loaderror', (event) => {
          if (cancelled || destroyedRef.current) return
          setError(event.error?.message ?? 'Failed to load the payment form.')
          setErrorCode('sdk_error')
          setIsLoading(false)
        })
        paymentElement.on('change', (event) => {
          if (cancelled || destroyedRef.current) return
          // MUST stay above the method dedup below. Stripe fires `change` on every
          // keystroke but the selected method rarely changes, so the early return
          // there would swallow nearly every completeness update — and for a
          // card-only intent (no accordion, method never changes) Save would never
          // enable at all.
          setIsComplete(!!event.complete)
          // Map Stripe's payment_method_types value → our neutral 'card' | 'bank'.
          // `null` = a type we have no mapping for (see toPaymentMethodType).
          //
          // An unknown method RESETS to 'card' rather than holding the previous
          // selection. This value picks the processing-fee rate on the checkout
          // summary, and the consuming state is sticky — so "hold" would let a
          // card → bank → (unknown) sequence leave the surcharge-exempt bank quote
          // on screen for a charge that won't be bank-priced. 'card' is the
          // surcharge-CHARGING default, the same one `resolveQuoteProcessingFee`
          // falls back to: over-quoting an unknown method is recoverable,
          // under-quoting a card charge costs the club real money.
          const method = toPaymentMethodType(event.value?.type) ?? 'card'
          if (method === lastMethodRef.current) return
          lastMethodRef.current = method
          onMethodChangeRef.current?.(method)
        })
        paymentElement.mount(target)
        element = paymentElement

        if (cancelled || destroyedRef.current) {
          paymentElement.destroy()
          elementsRef.current = null
        }
      } catch (err) {
        if (cancelled || destroyedRef.current) return
        setError(err instanceof Error ? err.message : 'Failed to initialize the payment form')
        setErrorCode('sdk_error')
        setIsLoading(false)
      }
    }

    void init()

    return () => {
      cancelled = true
      if (element) {
        try { element.destroy() } catch { /* ignore */ }
      }
      elementsRef.current = null
    }
  }, [isConfigured, publishableKey, clientSecret])

  useEffect(() => {
    return () => {
      destroyedRef.current = true
    }
  }, [])

  const confirmSetup = useCallback(async (): Promise<ConfirmSetupResult> => {
    const stripe = stripeRef.current
    const elements = elementsRef.current
    if (!stripe || !elements) {
      return { success: false, error: 'Payment form is not ready. Please try again.', isInfrastructureError: true }
    }

    try {
      // `redirect: 'if_required'` only navigates for payment methods that REQUIRE a
      // redirect. Our intents are card (`payment_method_types: ['card']`) and
      // us_bank_account FC-instant — both present 3DS / Financial Connections INLINE
      // (modal/iframe), so no full-page redirect occurs and `return_url` is never used.
      //
      // Stripe rejects a non-http(s) `return_url` outright (IntegrationError), and this
      // hook runs on origins that aren't: the mobile app is `capacitor://localhost` on
      // iOS (no `iosScheme` is set) while the SAME build also ships as a web app on
      // Fly. So resolve it at RUNTIME and omit `confirmParams` entirely rather than
      // send something invalid — it's optional, and unused on every method we enable.
      //
      // ⚠ If a redirect-based method is ever enabled, omission stops being free: a
      // "would redirect" case becomes a Stripe error instead of a navigation. Native
      // surfaces would then need a real https origin (an `iosScheme`, or a
      // `VITE_WEB_ORIGIN` passed in as `returnUrl`) — not just this guard.
      const resolvedReturnUrl =
        returnUrlRef.current ?? (typeof window !== 'undefined' ? window.location.href : '')
      const canUseReturnUrl = /^https?:/i.test(resolvedReturnUrl)
      const { setupIntent, error: stripeError } = await stripe.confirmSetup({
        elements,
        ...(canUseReturnUrl ? { confirmParams: { return_url: resolvedReturnUrl } } : {}),
        redirect: 'if_required',
      })

      if (stripeError) {
        return {
          success: false,
          error: stripeError.message ?? 'Payment confirmation failed',
          code: stripeError.code,
          // Specific issuer reason on a `card_declined` — `mapStripeCardError`
          // keys branded decline copy on this first, so it must be passed through.
          declineCode: stripeError.decline_code,
          errorType: stripeError.type,
          // Which instrument this confirm was for, so the copy mapper doesn't show
          // card wording ("Your card was declined") for a bank failure. Defaults to
          // 'card' when no change event has fired yet — see describeStripeConfirmError.
          method: lastMethodRef.current ?? 'card',
          // Report infra (api_connection_error/api_error) and fully unclassified
          // failures; a user cancelling / a validation error is not infra.
          isInfrastructureError:
            isStripeInfrastructureError({ type: stripeError.type, code: stripeError.code }) ||
            (!stripeError.type && !stripeError.code),
        }
      }

      if (!setupIntent?.id || setupIntent.status !== 'succeeded') {
        // A missing SetupIntent is a malformed response worth reporting; a resolved
        // but not-succeeded status (requires_action/processing — e.g. the user
        // closed the flow, or ACH microdeposits) is a user/Stripe state, not infra.
        return {
          success: false,
          error: 'Setup was not completed. Please try again.',
          isInfrastructureError: !setupIntent?.id,
        }
      }

      return { success: true, setupIntentId: setupIntent.id }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment confirmation failed'
      return { success: false, error: message, isInfrastructureError: true }
    }
  }, [])

  return { paymentElementRef, isReady, isComplete, isLoading, error, errorCode, confirmSetup }
}
